import { useMutation, useQuery } from "@tanstack/react-query";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useState, type FormEvent } from "react";
import { Banner, Button, Card, Field, Input, Select, Spinner } from "@magarac/ui";
import {
  MODULE_LABELS,
  MONEY_BACK_DAYS,
  PLAN_PRICE_CENTS,
  PLAN_SUMMARIES,
  SEAT_PRICE_CENTS,
} from "@magarac/shared/billing";
import { formatCents } from "@magarac/shared/money";
import { myTenantsSchema } from "@magarac/shared/schemas/me";
import { INDUSTRY_TEMPLATES, type IndustryTemplateKey } from "@magarac/shared/templates";
import { US_STATES } from "@magarac/shared/us-states";
import { z } from "zod";
import { Turnstile } from "../components/Turnstile";
import { api } from "../lib/api";
import { messageFor } from "../lib/errors";

export const Route = createFileRoute("/signup")({
  component: SignupPage,
});

function guessTimeZone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "America/Chicago";
  } catch {
    return "America/Chicago";
  }
}

type PlanId = (typeof PLAN_SUMMARIES)[number]["id"];

/**
 * ACC-9, BIL-5: sign up and start using J360 in one go: your login, your business, your plan.
 * Setup takes a few seconds; the page waits, then opens the business.
 */
function SignupPage() {
  const [form, setForm] = useState({
    businessName: "",
    yourName: "",
    email: "",
    password: "",
    industryTemplate: "hvac" as IndustryTemplateKey,
    state: "",
    planId: "team" as PlanId,
    acceptTerms: false,
  });
  const [problem, setProblem] = useState<string | null>(null);
  const [tenantId, setTenantId] = useState<string | null>(null);
  const [token, setToken] = useState<string | null>(null);
  // Bot-check tokens work once: after a failed attempt, show a fresh check.
  const [checkRound, setCheckRound] = useState(0);
  const config = useQuery({
    queryKey: ["signup-config"],
    queryFn: () =>
      api.get("/public/signup/config", z.object({ turnstileSiteKey: z.string().nullable() })),
    staleTime: Infinity,
  });
  const siteKey = config.data?.turnstileSiteKey ?? null;
  const set = (k: keyof typeof form) => (e: { target: { value: string } }) =>
    setForm({ ...form, [k]: e.target.value });
  const signup = useMutation({
    mutationFn: () =>
      api.post(
        "/public/signup",
        { ...form, timezone: guessTimeZone(), ...(token ? { turnstileToken: token } : {}) },
        z.object({ tenantId: z.string(), slug: z.string() }),
      ),
    onSuccess: (r) => setTenantId(r.tenantId),
    onError: () => {
      setToken(null);
      setCheckRound((n) => n + 1);
    },
  });
  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (!form.businessName.trim() || !form.yourName.trim() || !form.email.trim())
      return setProblem("Fill in your business name, your name, and your email.");
    if (form.password.length < 10)
      return setProblem("Choose a password of at least 10 characters.");
    if (!form.state) return setProblem("Choose the state your business is in.");
    if (!form.acceptTerms) return setProblem("Please accept the terms to continue.");
    if (siteKey && !token)
      return setProblem(
        "One moment: we are checking that you are a person. Try again in a second.",
      );
    setProblem(null);
    signup.mutate();
  };

  if (tenantId) return <SettingUp tenantId={tenantId} />;

  return (
    <main className="mx-auto max-w-2xl px-4 py-8">
      <h1 className="mb-1 text-2xl font-semibold">Start using J360</h1>
      <p className="mb-6 text-muted">
        Set up your business in about a minute. {MONEY_BACK_DAYS}-day money-back guarantee.
      </p>
      <form onSubmit={submit} noValidate>
        {problem || signup.error ? (
          <Banner tone="danger">{problem ?? messageFor(signup.error)}</Banner>
        ) : null}
        <Card title="Your business" className="mb-4">
          <Field id="su-business" label="Business name">
            <Input
              id="su-business"
              autoComplete="organization"
              value={form.businessName}
              onChange={set("businessName")}
            />
          </Field>
          <div className="grid gap-x-4 sm:grid-cols-2">
            <Field
              id="su-industry"
              label="Kind of business"
              hint="Sets up your services and wording."
            >
              <Select
                id="su-industry"
                value={form.industryTemplate}
                onChange={set("industryTemplate")}
              >
                {Object.entries(INDUSTRY_TEMPLATES).map(([key, t]) => (
                  <option key={key} value={key}>
                    {t.name}
                  </option>
                ))}
              </Select>
            </Field>
            <Field id="su-state" label="State">
              <Select id="su-state" value={form.state} onChange={set("state")}>
                <option value="">Choose</option>
                {US_STATES.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </Select>
            </Field>
          </div>
        </Card>

        <Card title="Your login" className="mb-4">
          <Field id="su-name" label="Your name">
            <Input
              id="su-name"
              autoComplete="name"
              value={form.yourName}
              onChange={set("yourName")}
            />
          </Field>
          <Field id="su-email" label="Email">
            <Input
              id="su-email"
              type="email"
              autoComplete="email"
              value={form.email}
              onChange={set("email")}
            />
          </Field>
          <Field id="su-password" label="Password" hint="At least 10 characters.">
            <Input
              id="su-password"
              type="password"
              autoComplete="new-password"
              value={form.password}
              onChange={set("password")}
            />
          </Field>
        </Card>

        <Card title="Your plan" className="mb-4">
          <ul className="space-y-2" role="radiogroup" aria-label="Plan">
            {PLAN_SUMMARIES.map((p) => (
              <li key={p.id}>
                <label
                  className={`flex cursor-pointer items-start gap-3 rounded-lg border p-3 ${
                    form.planId === p.id ? "border-brand-600 bg-brand-50" : "border-line"
                  }`}
                >
                  <input
                    type="radio"
                    name="plan"
                    className="mt-1"
                    checked={form.planId === p.id}
                    onChange={() => setForm({ ...form, planId: p.id })}
                  />
                  <span className="min-w-0 flex-1">
                    <span className="flex justify-between gap-2">
                      <span className="font-semibold">{p.name}</span>
                      <span className="tabular-nums">
                        {formatCents(PLAN_PRICE_CENTS[p.id] ?? 0)}/month
                      </span>
                    </span>
                    <span className="block text-sm text-muted">
                      {p.modules.map((m) => MODULE_LABELS[m]).join(", ")}. {p.seats}{" "}
                      {p.seats === 1 ? "person" : "people"} included
                      {p.minutes ? `, ${p.minutes} receptionist minutes` : ""}.
                    </span>
                  </span>
                </label>
              </li>
            ))}
          </ul>
          <p className="mt-2 text-sm text-muted">
            Extra people are {formatCents(SEAT_PRICE_CENTS)} a month each. You can change plans
            later.
          </p>
          <Banner tone="info">Test mode: no card is needed and nothing is charged yet.</Banner>
        </Card>

        <label className="mb-4 flex items-start gap-3">
          <input
            type="checkbox"
            className="mt-1 h-5 w-5"
            checked={form.acceptTerms}
            onChange={(e) => setForm({ ...form, acceptTerms: e.target.checked })}
          />
          <span>
            I agree to the J360 terms of service and privacy policy. If J360 is not right for you,
            ask within {MONEY_BACK_DAYS} days for a full refund.
          </span>
        </label>
        {siteKey ? <Turnstile key={checkRound} siteKey={siteKey} onToken={setToken} /> : null}
        <Button type="submit" className="w-full" busy={signup.isPending}>
          Create my account
        </Button>
        <p className="mt-4 text-center text-sm">
          Already use J360?{" "}
          <Link to="/login" className="text-brand-700 underline">
            Sign in
          </Link>
        </p>
      </form>
    </main>
  );
}

/** Waits for setup to finish (a few seconds), then opens the new business. */
function SettingUp({ tenantId }: { tenantId: string }) {
  const [slow, setSlow] = useState(false);
  useEffect(() => {
    let live = true;
    const started = Date.now();
    const tick = async () => {
      try {
        const me = await api.get("/me/tenants", myTenantsSchema);
        const t = me.tenants.find((x) => x.id === tenantId);
        if (t && t.status === "active") {
          await api.post("/me/switch", { tenantId }, z.unknown()).catch(() => undefined);
          window.location.assign("/");
          return;
        }
      } catch {
        // Keep trying: the login cookie or the business may not be ready this instant.
      }
      if (!live) return;
      if (Date.now() - started > 45_000) setSlow(true);
      setTimeout(() => void tick(), 1500);
    };
    void tick();
    return () => {
      live = false;
    };
  }, [tenantId]);
  return (
    <main className="mx-auto max-w-md px-4 py-16 text-center">
      <Spinner />
      <h1 className="mt-4 text-xl font-semibold">Setting up your business…</h1>
      <p className="mt-2 text-muted">We are adding your services, hours, and settings.</p>
      {slow ? (
        <Banner tone="warn">
          This is taking longer than usual. We have been told and will finish it for you; you can
          also sign in later with the email and password you just chose.
        </Banner>
      ) : null}
    </main>
  );
}
